// Verzoeken: het gesprek tussen lid, club en bond (fase 5 clubregister).
//
// Eén paneel voor alle drie de partijen. De database bepaalt wie een verzoek
// ziet, wie wat mag schrijven en wat een andere partij van een bericht te zien
// krijgt (tests/rls/26_verzoeken.sql); dit scherm legt voor en toont.
//
//   toonVerzoeken(el, client, { openen, schrijven })
//     openen:    functie die de keuzes voor een nieuw verzoek geeft,
//                [{ label, args }], of een lege lijst als dat niet kan
//     schrijven: mag de gebruiker schrijven (case.write; voor een lid altijd waar)
import { t, locale } from '../lib/i18n.js';
import { esc } from '../lib/format.js';
import { toast } from './toast.js';
import { actieKaartHtml, behandelLm, lidmLogTekst } from './lidmaatschap.js';

const TYPES_NU = ['aansluiting', 'geschil', 'overig'];
const BADGE = { open: 'badge-blue', wacht_op_reactie: 'badge-amber', afgehandeld: 'badge-grey', geescaleerd: 'badge-red' };

export function verzoekFout(e) {
  const m = String(e?.message || e || '').match(/verzoek:([a-z_]+)/);
  return m ? t('verz.fout.' + m[1]) : t('alg.fout', { melding: e?.message || String(e) });
}

const partij = p => t('verz.partij.' + p);
const datum = iso => {
  if (!iso) return '';
  const d = new Date(iso.length > 10 ? iso : iso + 'T00:00:00Z');
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(locale(), { day: 'numeric', month: 'short', year: 'numeric', timeZone: iso.length > 10 ? undefined : 'UTC' });
};
const datumTijd = iso => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(locale(), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};
const klein = tekst => `<p style="font-size:12px;color:var(--text-3)">${esc(tekst)}</p>`;
const knop = (actie, icoon, tekst, soort = 'btn-default', extra = '') =>
  `<button type="button" class="btn ${soort} btn-sm" data-verz="${actie}" ${extra}><i class="ti ti-${icoon}"></i> ${esc(tekst)}</button>`;

// ── Nieuw verzoek vanuit een ander scherm (C3: een rij in de ledenlijst) ────
// args: de vaste velden voor case_openen (p_bond, p_person, p_club_register).
// Geeft het id van het nieuwe verzoek, of null.
export function nieuwVerzoekVenster(client, { titel, uitleg, args }) {
  return new Promise(klaar => {
    document.getElementById('verzNieuwModal')?.remove();
    const m = document.createElement('div');
    m.className = 'modal-overlay open';
    m.id = 'verzNieuwModal';
    m.innerHTML = `<div class="modal" style="max-width:560px">
      <div class="modal-header"><span class="modal-title">${esc(titel)}</span>
        <button type="button" class="btn-icon" data-vn="annuleer" title="${esc(t('alg.sluiten'))}"><i class="ti ti-x"></i></button></div>
      <div class="modal-body">
        ${uitleg ? `<p style="font-size:13px;color:var(--text-2);margin-bottom:12px">${esc(uitleg)}</p>` : ''}
        <div class="form-group"><label class="form-label">${esc(t('verz.veld.type'))}</label>
          <select class="form-input" name="vn_type">${TYPES_NU.filter(x => x !== 'aansluiting').map(x => `<option value="${x}">${esc(t('verz.type.' + x))}</option>`).join('')}</select></div>
        <div class="form-group"><label class="form-label">${esc(t('verz.veld.onderwerp'))}</label><input class="form-input" name="vn_onderwerp" maxlength="200"></div>
        <div class="form-group"><label class="form-label">${esc(t('verz.veld.bericht'))}</label><textarea class="form-input" name="vn_bericht" rows="4" maxlength="4000"></textarea></div>
        <div class="form-group"><label class="form-label">${esc(t('verz.veld.zichtbaarheid'))}</label>
          <select class="form-input" name="vn_zicht">${['iedereen', 'club_bond'].map(w => `<option value="${w}">${esc(t('verz.zicht.' + w))}</option>`).join('')}</select>
          <div style="font-size:11px;color:var(--text-3);margin-top:3px">${esc(t('verz.zicht.uitleg'))}</div></div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-default" data-vn="annuleer">${esc(t('alg.annuleren'))}</button>
        <button type="button" class="btn btn-primary" data-vn="ok"><i class="ti ti-send"></i> ${esc(t('verz.knop.openen'))}</button>
      </div></div>`;
    document.body.appendChild(m);
    const veld = n => m.querySelector(`[name="${n}"]`)?.value.trim() || '';
    m.addEventListener('click', async e => {
      if (e.target === m || e.target.closest('[data-vn="annuleer"]')) { m.remove(); return klaar(null); }
      const ok = e.target.closest('[data-vn="ok"]');
      if (!ok || ok.disabled) return;
      ok.disabled = true;
      const { data, error } = await client.rpc('case_openen', {
        p_type: veld('vn_type') || 'overig', p_onderwerp: veld('vn_onderwerp'), p_bericht: veld('vn_bericht'),
        p_zichtbaarheid: veld('vn_zicht') || 'iedereen', ...args });
      ok.disabled = false;
      if (error) { toast(verzoekFout(error), 'error'); return; }
      toast(t('verz.geopend'), 'success');
      m.remove();
      klaar(data);
    });
  });
}

// ── Venster voor een statuswijziging ───────────────────────────────────────
function vraagStatus({ titel, uitleg, wachtOp = [], knopTekst }) {
  return new Promise(klaar => {
    document.getElementById('verzModal')?.remove();
    const m = document.createElement('div');
    m.className = 'modal-overlay open';
    m.id = 'verzModal';
    m.innerHTML = `<div class="modal" style="max-width:520px">
      <div class="modal-header"><span class="modal-title">${esc(titel)}</span>
        <button type="button" class="btn-icon" data-verz-m="annuleer" title="${esc(t('alg.sluiten'))}"><i class="ti ti-x"></i></button></div>
      <div class="modal-body">
        ${uitleg ? `<p style="font-size:13px;color:var(--text-2);margin-bottom:12px">${esc(uitleg)}</p>` : ''}
        ${wachtOp.length ? `<div class="form-row">
          <div class="form-group"><label class="form-label">${esc(t('verz.wacht.op'))}</label>
            <select class="form-input" name="vWacht">${wachtOp.map(p => `<option value="${esc(p)}">${esc(partij(p))}</option>`).join('')}</select></div>
          <div class="form-group"><label class="form-label">${esc(t('verz.wacht.termijn'))}</label>
            <input class="form-input" type="date" name="vTermijn"></div></div>` : ''}
        <div class="form-group"><label class="form-label">${esc(t('verz.opmerking'))}</label>
          <textarea class="form-input" name="vOpmerking" rows="3" maxlength="4000"></textarea></div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-default" data-verz-m="annuleer">${esc(t('alg.annuleren'))}</button>
        <button type="button" class="btn btn-primary" data-verz-m="ok">${esc(knopTekst)}</button>
      </div></div>`;
    document.body.appendChild(m);
    const sluit = uit => { m.remove(); klaar(uit); };
    m.addEventListener('click', e => {
      if (e.target === m || e.target.closest('[data-verz-m="annuleer"]')) return sluit(null);
      if (!e.target.closest('[data-verz-m="ok"]')) return;
      sluit({
        wachtOp: m.querySelector('[name="vWacht"]')?.value || null,
        termijn: m.querySelector('[name="vTermijn"]')?.value || null,
        opmerking: m.querySelector('[name="vOpmerking"]')?.value.trim() || null,
      });
    });
  });
}

// ── Het paneel ─────────────────────────────────────────────────────────────
export async function toonVerzoeken(el, client, opties = {}) {
  const schrijven = opties.schrijven !== false;
  const staat = { rijen: [], acties: [], open: null, berichten: [], keuzes: [], formulier: false };
  const rpc = (naam, args) => client.rpc(naam, args);

  async function laadLijst() {
    const { data, error } = await rpc('cases_lijst');
    if (error) return false;
    staat.rijen = data || [];
    // Wijzigingen van het lidmaatschap horen bij een verzoek; ontbreken ze, dan is dat geen fout.
    const ac = await rpc('lidmaatschap_acties_lijst');
    staat.acties = ac.error ? [] : ac.data || [];
    return true;
  }

  async function teken() {
    if (staat.open) return tekenDraad();
    el.hidden = false;
    const open = staat.rijen.filter(r => r.status !== 'afgehandeld');
    const klaar = staat.rijen.filter(r => r.status === 'afgehandeld');
    // Wat op uw besluit wacht en waarvan u het verzoek niet kunt openen (bijvoorbeeld de nieuwe club).
    const zichtbaar = new Set(staat.rijen.map(r => r.id));
    const wacht = staat.acties.filter(a => a.status === 'open' && !a.geescaleerd && !zichtbaar.has(a.case_id)
      && (a.rollen || []).some(x => x.status === 'wacht' && x.kan));
    el.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:10px;flex-wrap:wrap">
        <div><h3 style="margin:0">${esc(t('verz.titel'))}</h3>
          <div class="settings-desc">${esc(t(opties.uitleg || 'verz.uitleg.org'))}</div></div>
        ${schrijven && staat.keuzes.length && !staat.formulier ? knop('nieuw', 'plus', t('verz.knop.nieuw'), 'btn-primary') : ''}
      </div>
      ${staat.formulier ? formulierHtml() : ''}
      ${wacht.length ? `<div class="section-title" style="margin-top:12px"><i class="ti ti-hourglass"></i> ${esc(t('lidm.sectie.wacht'))}</div>${wacht.map(actieKaartHtml).join('')}` : ''}
      ${open.map(rijHtml).join('') || (klaar.length ? '' : klein(t('verz.leeg')))}
      ${klaar.length ? `<div class="section-title" style="margin-top:12px"><i class="ti ti-circle-check"></i> ${esc(t('verz.sectie.afgehandeld'))}</div>${klaar.map(rijHtml).join('')}` : ''}`;
  }

  const rijHtml = r => {
    const over = [r.club_naam, r.persoon_naam].filter(Boolean).join(' · ');
    const regel = [r.ref, t('verz.type.' + r.type), over, opties.metBond ? r.bond_naam : '',
      r.laatste_bericht_op ? t('verz.laatste', { datum: datumTijd(r.laatste_bericht_op) }) : ''].filter(Boolean);
    return `<div class="org-row" style="align-items:flex-start;cursor:pointer" data-verz="open" data-id="${esc(r.id)}">
      <div style="flex:1;min-width:220px">
        <div style="font-size:13px;font-weight:600">${esc(r.onderwerp)}
          <span class="badge ${BADGE[r.status] || 'badge-grey'}">${esc(t('verz.status.' + r.status))}</span>
          ${r.wacht_op_mij ? `<span class="badge badge-amber">${esc(t('verz.wacht.opu'))}</span>` : ''}
          ${r.nieuw ? `<span class="badge badge-teal">${esc(t('verz.nieuw'))}</span>` : ''}</div>
        <div style="font-size:11px;color:var(--text-3);margin-top:3px">${esc(regel.join(' · '))}</div>
      </div><i class="ti ti-chevron-right" style="color:var(--text-3)"></i></div>`;
  };

  function formulierHtml() {
    const keuze = staat.keuzes.length > 1
      ? `<div class="form-group" style="flex:2"><label class="form-label">${esc(t('verz.veld.met'))}</label>
           <select class="form-input" name="vz_met">${staat.keuzes.map((k, i) => `<option value="${i}">${esc(k.label)}</option>`).join('')}</select></div>`
      : `<div class="form-group" style="flex:2"><label class="form-label">${esc(t('verz.veld.met'))}</label><div style="font-size:13px;padding-top:8px">${esc(staat.keuzes[0].label)}</div></div>`;
    return `<div style="border:1px solid var(--border);border-radius:var(--radius);padding:14px;margin:12px 0">
      <div class="form-row">${keuze}
        <div class="form-group"><label class="form-label">${esc(t('verz.veld.type'))}</label>
          <select class="form-input" name="vz_type">${TYPES_NU.map(x => `<option value="${x}">${esc(t('verz.type.' + x))}</option>`).join('')}</select></div></div>
      <div class="form-group"><label class="form-label">${esc(t('verz.veld.onderwerp'))}</label><input class="form-input" name="vz_onderwerp" maxlength="200"></div>
      <div class="form-group"><label class="form-label">${esc(t('verz.veld.bericht'))}</label><textarea class="form-input" name="vz_bericht" rows="4" maxlength="4000"></textarea></div>
      ${opties.zichtbaarheidKeuze ? zichtSelect('vz_zicht', ['iedereen', 'club_bond']) : ''}
      <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:8px">
        ${knop('nieuw-annuleer', 'x', t('alg.annuleren'), 'btn-ghost')}${knop('nieuw-opslaan', 'send', t('verz.knop.openen'), 'btn-primary')}</div>
    </div>`;
  }

  const zichtSelect = (naam, waarden) => `<div class="form-group"><label class="form-label">${esc(t('verz.veld.zichtbaarheid'))}</label>
    <select class="form-input" name="${naam}">${waarden.map(w => `<option value="${w}">${esc(t('verz.zicht.' + w))}</option>`).join('')}</select>
    <div style="font-size:11px;color:var(--text-3);margin-top:3px">${esc(t('verz.zicht.uitleg'))}</div></div>`;

  // ── Draad ────────────────────────────────────────────────────────────────
  const huidig = () => staat.rijen.find(r => r.id === staat.open);

  function logTekst(m) {
    const wie = partij(m.auteur_partij);
    const meta = m.meta || {};
    if (meta.actie) return lidmLogTekst(meta, wie);
    if (meta.status === 'afgehandeld') return t('verz.log.afgehandeld', { partij: wie });
    if (meta.status === 'geescaleerd') return t('verz.log.geescaleerd', { partij: wie });
    if (meta.status === 'wacht_op_reactie') {
      return t('verz.log.wacht', { partij: wie, wacht: partij(meta.wacht_op) }) + (meta.termijn ? ' ' + t('verz.log.termijn', { datum: datum(meta.termijn) }) : '');
    }
    return t(meta.van === 'afgehandeld' ? 'verz.log.heropend' : 'verz.log.open', { partij: wie });
  }

  function berichtHtml(m) {
    if (m.kind === 'status') {
      return `<div style="text-align:center;font-size:12px;color:var(--text-3);margin:10px 0">
        ${esc(logTekst(m))} &middot; ${esc(datumTijd(m.created_at))}${m.tekst ? `<div style="color:var(--text-2);margin-top:2px">“${esc(m.tekst)}”</div>` : ''}</div>`;
    }
    const kop = [partij(m.partij), m.auteur].filter(Boolean).join(' · ');
    return `<div style="display:flex;flex-direction:column;align-items:${m.van_mij ? 'flex-end' : 'flex-start'};margin:10px 0">
      <div style="max-width:min(560px,92%);background:${m.van_mij ? 'var(--accent-light)' : 'var(--surface-2)'};border-radius:var(--radius);padding:8px 12px">
        <div style="font-size:11px;color:var(--text-3);margin-bottom:3px">${esc(kop)} &middot; ${esc(datumTijd(m.created_at))}
          ${m.zichtbaarheid !== 'iedereen' ? ` &middot; <i class="ti ti-lock" style="font-size:11px"></i> ${esc(t('verz.zicht.' + m.zichtbaarheid))}` : ''}</div>
        <div style="font-size:13px;white-space:pre-wrap;word-break:break-word">${esc(m.tekst)}</div>
        ${m.namens ? `<div style="font-size:11px;color:var(--text-3);margin-top:4px"><i class="ti ti-user-share" style="font-size:11px"></i> ${esc(t('verz.namens.bericht', { partij: partij(m.auteur_partij) }))}</div>` : ''}
      </div></div>`;
  }

  function tekenDraad() {
    const r = huidig();
    if (!r) { staat.open = null; return teken(); }
    const mijn = r.mijn_partijen || [];
    const kanSchrijven = schrijven && mijn.length > 0 && r.status !== 'afgehandeld';
    const over = [r.club_naam, r.persoon_naam].filter(Boolean).join(' · ');
    const bond = mijn.includes('bond');
    const kanAfhandelen = mijn.includes('bond') || mijn.includes(r.aangemaakt_door_partij);
    const namens = bond ? [
      !r.club_org_id && r.club_register_id ? 'club' : null,
      r.person_id && !r.persoon_heeft_inlog ? 'lid' : null].filter(Boolean) : [];
    const schrijfbaar = mijn.filter(p => p !== 'platform');
    el.innerHTML = `
      <div style="display:flex;gap:8px;align-items:center;margin-bottom:10px">${knop('terug', 'arrow-left', t('verz.knop.terug'), 'btn-ghost')}</div>
      <div style="display:flex;justify-content:space-between;gap:10px;flex-wrap:wrap;align-items:flex-start">
        <div><h3 style="margin:0">${esc(r.onderwerp)}</h3>
          <div style="font-size:11px;color:var(--text-3);margin-top:3px">${esc([r.ref, t('verz.type.' + r.type), over, opties.metBond ? r.bond_naam : ''].filter(Boolean).join(' · '))}</div></div>
        <div><span class="badge ${BADGE[r.status] || 'badge-grey'}">${esc(t('verz.status.' + r.status))}</span>
          ${r.status === 'wacht_op_reactie' ? `<span class="badge badge-amber">${esc(t('verz.wacht.status', { partij: partij(r.wacht_op) }))}</span>` : ''}</div>
      </div>
      ${r.termijn_tot ? klein(t('verz.termijn', { datum: datum(r.termijn_tot) })) : ''}
      ${staat.acties.filter(a => a.case_id === r.id).map(actieKaartHtml).join('')}
      ${r.meekijken && !mijn.length ? `<div class="banner banner-amber" style="margin-top:10px"><i class="ti ti-eye"></i><span>${esc(t('verz.meekijken'))}</span></div>` : ''}
      ${kanSchrijven ? `<div style="display:flex;gap:6px;flex-wrap:wrap;margin:10px 0">
        ${knop('wachten', 'hourglass', t('verz.knop.wachten'))}
        ${kanAfhandelen ? knop('afhandelen', 'circle-check', t('verz.knop.afhandelen')) : ''}
        ${r.status === 'geescaleerd' && bond ? knop('heropenen', 'rotate', t('verz.knop.opnieuw')) : knop('escaleren', 'alert-triangle', t('verz.knop.escaleren'), 'btn-ghost')}
      </div>` : ''}
      ${schrijven && bond && r.status === 'afgehandeld' ? `<div style="margin:10px 0">${knop('heropenen', 'rotate', t('verz.knop.heropenen'))}</div>` : ''}
      <div id="verzDraad" style="margin-top:6px">${staat.berichten.map(berichtHtml).join('')}</div>
      ${kanSchrijven ? `<div style="border-top:1px solid var(--border);padding-top:12px;margin-top:8px">
        <div class="form-group"><textarea class="form-input" name="vb_tekst" rows="3" maxlength="4000" placeholder="${esc(t('verz.veld.antwoord'))}"></textarea></div>
        <div class="form-row" style="align-items:flex-end">
          ${mijn.includes('club') || bond ? `<div class="form-group">${zichtSelect('vb_zicht', r.person_id ? ['iedereen', 'club_bond', 'eigen'] : ['iedereen', 'eigen'])}</div>` : ''}
          ${schrijfbaar.length > 1 ? `<div class="form-group"><label class="form-label">${esc(t('verz.veld.als'))}</label>
            <select class="form-input" name="vb_als">${schrijfbaar.map(p => `<option value="${p}">${esc(partij(p))}</option>`).join('')}</select></div>` : ''}
          ${namens.length ? `<div class="form-group"><label class="form-label">${esc(t('verz.veld.namens'))}</label>
            <select class="form-input" name="vb_namens"><option value="">${esc(t('verz.namens.nee'))}</option>
              ${namens.map(p => `<option value="${p}">${esc(t('verz.namens.optie', { partij: partij(p) }))}</option>`).join('')}</select></div>` : ''}
          <div style="margin-left:auto">${knop('versturen', 'send', t('verz.knop.versturen'), 'btn-primary')}</div>
        </div></div>` : ''}`;
    const draad = el.querySelector('#verzDraad');
    if (draad) draad.scrollTop = draad.scrollHeight;
  }

  async function openDraad(id) {
    const { data, error } = await rpc('case_berichten_lijst', { p_case: id });
    if (error) { toast(verzoekFout(error), 'error'); return; }
    staat.open = id;
    staat.berichten = data || [];
    teken();
    // Nu gelezen; de teller in de lijst wordt bij terugkeren ververst.
    rpc('case_gelezen', { p_case: id });
  }

  async function ververs(melding) {
    if (melding) toast(melding, 'success');
    await laadLijst();
    if (staat.open) {
      const { data } = await rpc('case_berichten_lijst', { p_case: staat.open });
      staat.berichten = data || [];
    }
    teken();
  }

  const veld = naam => el.querySelector(`[name="${naam}"]`)?.value?.trim() || '';
  const aanroep = async (naam, args, melding) => {
    const { data, error } = await rpc(naam, args);
    if (error) { toast(verzoekFout(error), 'error'); return null; }
    await ververs(melding);
    return data ?? true;
  };

  el.onclick = async e => {
    const lm = e.target.closest('[data-lm]');
    if (lm && !lm.disabled) return behandelLm(lm, client, () => ververs());
    const k = e.target.closest('[data-verz]');
    if (!k || k.disabled) return;
    const a = k.dataset.verz;
    const r = huidig();
    if (a === 'open') return openDraad(k.dataset.id);
    if (a === 'terug') { staat.open = null; staat.berichten = []; await laadLijst(); return teken(); }
    if (a === 'nieuw') { staat.formulier = true; return teken(); }
    if (a === 'nieuw-annuleer') { staat.formulier = false; return teken(); }
    if (a === 'nieuw-opslaan') {
      const keuze = staat.keuzes[Number(veld('vz_met') || 0)];
      if (!keuze) return;
      const { data, error } = await rpc('case_openen', {
        p_type: veld('vz_type') || 'overig', p_onderwerp: veld('vz_onderwerp'), p_bericht: veld('vz_bericht'),
        p_zichtbaarheid: veld('vz_zicht') || 'iedereen', ...keuze.args });
      if (error) { toast(verzoekFout(error), 'error'); return; }
      staat.formulier = false;
      toast(t('verz.geopend'), 'success');
      await laadLijst();
      return openDraad(data);
    }
    if (a === 'versturen' && r) {
      const tekst = veld('vb_tekst');
      if (!tekst) return;
      const args = { p_case: r.id, p_tekst: tekst, p_zichtbaarheid: veld('vb_zicht') || 'iedereen',
        p_namens: veld('vb_namens') || null, p_als: veld('vb_als') || null };
      return aanroep('case_bericht', args, null);
    }
    if (!r) return;
    const status = (nieuw, extra = {}) => aanroep('case_status', { p_case: r.id, p_status: nieuw, p_wacht_op: null, p_termijn: null,
      p_opmerking: null, p_als: (r.mijn_partijen || []).length > 1 ? veld('vb_als') || null : null, ...extra });
    if (a === 'wachten') {
      const mijn = r.mijn_partijen || [];
      const wie = ['bond', r.club_org_id ? 'club' : null, r.person_id ? 'lid' : null].filter(p => p && !(mijn.length === 1 && mijn[0] === p));
      const uit = await vraagStatus({ titel: t('verz.wachten.titel'), uitleg: t('verz.wachten.uitleg'), wachtOp: wie, knopTekst: t('verz.knop.wachten') });
      if (!uit) return;
      return status('wacht_op_reactie', { p_wacht_op: uit.wachtOp, p_termijn: uit.termijn, p_opmerking: uit.opmerking });
    }
    if (a === 'afhandelen' || a === 'escaleren' || a === 'heropenen') {
      const nieuw = { afhandelen: 'afgehandeld', escaleren: 'geescaleerd', heropenen: 'open' }[a];
      const uit = await vraagStatus({ titel: t('verz.' + a + '.titel'), uitleg: t('verz.' + a + '.uitleg'), knopTekst: t('verz.knop.' + a) });
      if (!uit) return;
      return status(nieuw, { p_opmerking: uit.opmerking });
    }
  };

  el.innerHTML = '<div class="spinner"></div>';
  const gelukt = await laadLijst();
  if (!gelukt) { el.innerHTML = ''; el.hidden = true; return false; }
  staat.keuzes = opties.openen ? (await opties.openen().catch(() => [])) || [] : [];
  // Zonder verzoeken en zonder mogelijkheid er een te openen valt er niets te tonen.
  if (!staat.rijen.length && !staat.keuzes.length && opties.verbergLeeg) { el.innerHTML = ''; el.hidden = true; return false; }
  teken();
  return true;
}
